import type { ReactNode } from "react";
import { Box } from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { alpha } from "@mui/material/styles";
import { APP_ENABLED_BORDER_COLOR } from "@/theme";
import type { NarrationModeOption } from "@/types/api";
import { BOOK_UPLOAD_DROPZONE_ACCEPT } from "@/utils/constants";

export const NATIVE_BOOK_FILE_INPUT_ACCEPT = Object.entries(BOOK_UPLOAD_DROPZONE_ACCEPT)
  .flatMap(([mime, exts]) => [mime, ...exts])
  .join(",");

const ALLOWED_BOOK_EXTENSIONS = new Set(Object.values(BOOK_UPLOAD_DROPZONE_ACCEPT).flatMap((exts) => exts.map((ext) => ext.toLowerCase())));

export function isAllowedBookFileExtension(ext: string): boolean {
  return ALLOWED_BOOK_EXTENSIONS.has(ext.toLowerCase());
}

/** Shared styling/data helpers for the "Project setup" page - both the post-upload flow and the
 * "Generate breakdown" fallback edit the exact same project fields, just with slightly different
 * affordances (file actions, model picker, narration applicability). */

export const outlinedNotchedOutlineSelector = "& .MuiOutlinedInput-notchedOutline" as const;
export const inputTextSelector = "& .MuiInputBase-input" as const;
export const borderEnabled = APP_ENABLED_BORDER_COLOR;

/** Figma `common/overlays/reversed/4p` token - the main content box's background. */
export const overlaysReversed4p = (theme: { palette: { common: { white: string } } }) => alpha(theme.palette.common.white, 0.04);

/** Figma `Elevation/8` dark-mode background - the uploaded-file card's fill. */
export const PAPER_ELEVATION_8_BG = "#2e2e2e" as const;

/** `aistudiolib.types.TargetAudience.FAMILY_VIEWING.value` - the catch-all option, mutually exclusive
 * with every other Target audience option (picking it clears the rest, and vice versa). */
export const ALL_AGES_TARGET_AUDIENCE = "Family viewing (All ages)" as const;

/** `aistudiolib.types.NarrationMode.NONE.value` - reproduces the pipeline's original hardcoded
 * no-narration behavior, so it's the safe default when a project has no `narration_mode` yet. */
export const DEFAULT_NARRATION_MODE = "none" as const;

export const FALLBACK_NARRATION_MODE_OPTIONS: readonly NarrationModeOption[] = [
  { narration_mode: "none", display_name: "None (replace narration with action, behavior, environment, and dialogue)" },
  { narration_mode: "preserve", display_name: "Preserve (keep narration only where it already exists, and do not add more)" },
  {
    narration_mode: "hybrid",
    display_name: "Hybrid (use narration only for bookends or transitions: opening, ending, time jumps, location changes)"
  }
];

const ADD_NEW_OPTION_PREFIX = "\u0000add-new:" as const;

export function addNewOptionValue(inputValue: string): string {
  return `${ADD_NEW_OPTION_PREFIX}${inputValue}`;
}

export function isAddNewOption(option: string): boolean {
  return option.startsWith(ADD_NEW_OPTION_PREFIX);
}

export function addNewOptionLabel(option: string): string {
  return option.slice(ADD_NEW_OPTION_PREFIX.length);
}

const creatableFilterOptions = createFilterOptions<string>();

/** Genre/Tone-and-mood are "creatable": pick from the list, or type something new and get an
 * "Add "<value>"" option appended (unless it already matches an existing/selected value). */
export function filterCreatableOptions(options: readonly string[], inputValue: string, selectedValues: readonly string[] = []): string[] {
  const filtered = creatableFilterOptions([...options], { inputValue, getOptionLabel: (option) => option });
  const trimmed = inputValue.trim();
  if (trimmed.length === 0) {
    return filtered;
  }

  const normalized = trimmed.toLowerCase();
  const alreadyExists =
    options.some((option) => option.trim().toLowerCase() === normalized) ||
    selectedValues.some((value) => value.trim().toLowerCase() === normalized);
  return alreadyExists ? filtered : [...filtered, addNewOptionValue(trimmed)];
}

export function dedupeCaseInsensitive(values: readonly string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const raw of values) {
    const trimmed = raw.trim();
    if (trimmed.length === 0) {
      continue;
    }

    const key = trimmed.toLowerCase();
    if (seen.has(key)) {
      continue;
    }

    seen.add(key);
    result.push(trimmed);
  }

  return result;
}

const LANGUAGE_CODE_BY_NAME: Record<string, string> = {
  English: "EN",
  Spanish: "ES",
  French: "FR",
  German: "DE",
  Italian: "IT",
  Portuguese: "PT",
  Dutch: "NL",
  Russian: "RU",
  Japanese: "JA",
  Korean: "KO",
  Chinese: "ZH",
  Arabic: "AR",
  Hindi: "HI",
  Hebrew: "HE"
};

const NATIVE_LANGUAGE_NAME_BY_NAME: Record<string, string> = {
  English: "English",
  Spanish: "Español",
  French: "Français",
  German: "Deutsch",
  Italian: "Italiano",
  Portuguese: "Português",
  Dutch: "Nederlands",
  Russian: "Русский",
  Japanese: "日本語",
  Korean: "한국어",
  Chinese: "中文",
  Arabic: "العربية",
  Hindi: "हिन्दी",
  Hebrew: "עברית"
};

/** Always the English 2-letter code, regardless of which language is picked - shown collapsed. */
export function languageCode(name: string): string {
  return LANGUAGE_CODE_BY_NAME[name] ?? name.slice(0, 2).toUpperCase();
}

/** Each language's own name in its own script - shown in the dropdown list. */
export function nativeLanguageName(name: string): string {
  return NATIVE_LANGUAGE_NAME_BY_NAME[name] ?? name;
}

/** Suffixes a field label with a red required-field asterisk. */
export function requiredLabel(label: string): ReactNode {
  return (
    <>
      {label}
      <Box component="span" sx={{ color: "error.main" }}>
        {" *"}
      </Box>
    </>
  );
}

const TARGET_RUNTIME_PATTERN = /^(\d{0,3})(?:(\.)(\d{0,2}))?/;

/** Clamps free-typed/pasted text to a valid "Target runtime" value: up to 3 integer digits, optionally
 * followed by a decimal point and up to 2 decimal digits (e.g. "3.25", "2.12"). Always returns a valid,
 * possibly-truncated string rather than rejecting outright, so invalid extra characters get visibly
 * dropped instead of silently staying in the (otherwise controlled) input. */
export function sanitizeTargetRuntimeInput(raw: string): string {
  const match = TARGET_RUNTIME_PATTERN.exec(raw);
  if (!match) {
    return "";
  }

  const [, integerPart = "", dot, decimalPart = ""] = match;
  return dot === undefined ? integerPart : `${integerPart}.${decimalPart}`;
}

/** `null` means "not provided" - matches `ProjectSettingsFormValues.targetRuntime`. A lone "." (dot just
 * typed, no digits yet) also parses to `null` rather than `NaN`/`0`. */
export function parseTargetRuntimeInput(raw: string): number | null {
  if (raw.length === 0 || raw === ".") {
    return null;
  }

  const parsed = Number.parseFloat(raw);
  return Number.isNaN(parsed) ? null : parsed;
}

export function formatTargetRuntimeInput(value: number | null): string {
  return value === null ? "" : String(value);
}

export interface ProjectSettingsFormValues {
  readonly genre: string | null;
  readonly targetAudience: readonly string[];
  readonly toneMood: readonly string[];
  readonly language: string | null;
  /** Target runtime in minutes; `null` means "not provided" (backend treats it as optional). */
  readonly targetRuntime: number | null;
  /** One of `NarrationModeOption["narration_mode"]`; always a concrete value (defaults to `"none"`). */
  readonly narrationMode: string;
  /** Free text, optional. */
  readonly creativeNotes: string;
  /** Which text model script generation should use for the "story" track; irrelevant otherwise. */
  readonly textModel: string | null;
}

/** The Project setup form's blank-slate values, before anything has been loaded from the project or
 * edited by the user. Shared so `ActionStore` (which persists the in-progress form across tab-switch
 * remounts) and `useScreenplayScriptFlow` agree on exactly what "untouched" means. */
export const DEFAULT_PROJECT_SETTINGS_FORM_VALUES: ProjectSettingsFormValues = {
  genre: null,
  targetAudience: [],
  toneMood: [],
  language: null,
  targetRuntime: null,
  narrationMode: "none",
  creativeNotes: "",
  textModel: null
};

/** Every "General" field is mandatory except Creative notes (its own section, always optional).
 * Narration/language always carry a concrete default so they're never actually empty; runtime
 * is the one field that genuinely needs a user-entered value. */
export function hasRequiredProjectSettings(values: ProjectSettingsFormValues): boolean {
  return (
    values.genre !== null &&
    values.genre.trim().length > 0 &&
    values.targetAudience.length > 0 &&
    values.toneMood.length > 0 &&
    values.targetRuntime !== null &&
    values.language !== null
  );
}
